/**
 * Sampling Indicator Component
 *
 * DC-326: Badge showing whether full or sampled analysis was used
 *
 * Displays:
 * - Full Analysis: Green badge when all rows were analyzed
 * - Sampled: Blue badge with sample size / total rows when sampling was used
 */

import React from 'react';
import './ai-readiness.css';

// =============================================================================
// TYPES
// =============================================================================

export interface SamplingIndicatorProps {
  /** Whether sampling was used for the analysis */
  samplingUsed: boolean;
  /** Number of rows in the sample (when sampling was used) */
  sampleSize?: number | null;
  /** Total number of rows in the dataset */
  totalRows?: number | null;
  /** Optional additional CSS class */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const SamplingIndicator: React.FC<SamplingIndicatorProps> = ({
  samplingUsed,
  sampleSize,
  totalRows,
  className = '',
}) => {
  if (!samplingUsed) {
    return (
      <div
        className={`ai-sampling-indicator ai-sampling-full ${className}`}
        title="All rows in the dataset were analyzed"
      >
        <span className="ai-sampling-icon">✓</span>
        <span className="ai-sampling-text">Full Analysis</span>
      </div>
    );
  }

  // Format numbers with commas for readability
  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '?';
    return num.toLocaleString();
  };

  const samplePercent = sampleSize && totalRows
    ? Math.round((sampleSize / totalRows) * 100)
    : null;

  const tooltipText = samplePercent
    ? `Analyzed ${formatNumber(sampleSize)} of ${formatNumber(totalRows)} rows (${samplePercent}% sample)`
    : 'Statistical sampling was used for this analysis';

  return (
    <div
      className={`ai-sampling-indicator ai-sampling-sampled ${className}`}
      title={tooltipText}
    >
      <span className="ai-sampling-icon">⚡</span>
      <span className="ai-sampling-text">
        Sampled: {formatNumber(sampleSize)} / {formatNumber(totalRows)}
      </span>
      {samplePercent !== null && (
        <span className="ai-sampling-percent">({samplePercent}%)</span>
      )}
    </div>
  );
};

export default SamplingIndicator;
